import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { savePromoToBackend } from '../../api/promoClient'
import '../Auth.css'

export default function SignIn() {
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [inviteMode, setInviteMode] = useState(false)
  const [inviteToken, setInviteToken] = useState<string | null>(null)
  const [promoCode, setPromoCode] = useState('')
  const [promoApplied, setPromoApplied] = useState(false)
  const { signIn } = useAuth()
  const navigate = useNavigate()

  // ============================================
  // EFFECT: Check for invite token & promo code
  // ============================================
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    
    // Check for invite token
    const token = urlParams.get('invite') || localStorage.getItem('inviteToken')
    if (token) {
      console.log('🎯 Invite token detected:', token)
      setInviteMode(true)
      setInviteToken(token)
      localStorage.setItem('inviteToken', token)
    }
    
    // Check for promo code in URL
    const urlPromo = urlParams.get('promo')
    if (urlPromo) {
      console.log('🎁 Promo code detected:', urlPromo)
      setPromoCode(urlPromo)
      setPromoApplied(true)
      
      const promoData = {
  code: promoCode,
  discountPercent: 10,    // Changed from 20 to 10
  freeMonths: 3,          // Changed from 2 to 3
  appliedAt: new Date().toISOString(),
  source: 'url_param'
}
      localStorage.setItem('appliedPromo', JSON.stringify(promoData))
    }
    
    // Check for promo code in localStorage
    const storedPromo = localStorage.getItem('appliedPromo')
    if (!urlPromo && storedPromo) {
      try {
        const promoData = JSON.parse(storedPromo)
        setPromoCode(promoData.code)
        setPromoApplied(true)
        console.log('🎁 Promo loaded from storage:', promoData.code)
      } catch (e) {
        console.error('Failed to parse stored promo:', e)
      }
    }
  }, [])

  // ============================================
  // HANDLER: Save promo to backend
  // ============================================
  const handlePromoSave = async (userData?: any) => {
    const promoStr = localStorage.getItem('appliedPromo')
    
    if (promoStr && userData) {
      try {
        const promoData = JSON.parse(promoStr)
        const userId = userData.id || userData.userId || userData.uid
        
        if (userId) {
          await savePromoToBackend(userId, promoData)
          console.log('✅ Promo saved to backend:', promoData.code)
          
          localStorage.removeItem('appliedPromo')
          setPromoApplied(false)
          setPromoCode('')
        }
      } catch (error) {
        console.error('Failed to save promo to backend:', error)
      }
    }
  }

  // ============================================
  // HANDLER: Handle authentication success
  // ============================================
  const handleAuthSuccess = async () => {
    await handlePromoSave()
    
    const token = localStorage.getItem('inviteToken')
    
    if (token) {
      try {
        const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api'
        const authToken = localStorage.getItem('accessToken') || localStorage.getItem('token')
        
        const response = await fetch(`${API_URL}/team/accept/${token}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        })
        
        const data = await response.json()
        
        if (data.success) {
          console.log('✅ Invitation accepted!')
          toast.success('🎉 You have joined the team!')
        }
        
        localStorage.removeItem('inviteToken')
        setInviteMode(false)
        setInviteToken(null)
      } catch (error) {
        console.error('Failed to accept invite:', error)
      }
    }
    
    navigate('/dashboard')
  }

  // ============================================
  // HANDLER: Form Submit
  // ============================================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const result = await signIn(email, password)
      await handlePromoSave(result)
      toast.success('Logged in successfully!')
      await handleAuthSuccess()
    } catch (error: any) {
      if (error.code === 'UNVERIFIED_EMAIL' || error.requiresVerification) {
        console.log('📧 Account exists but NOT verified.')
        toast.error('Please verify your email to continue')
        navigate(`/verify-email?email=${encodeURIComponent(email)}`)
        return
      }

      toast.error(error.message || 'Sign in failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="auth-container">
      <Link to="/" className="back-to-landing-button">
        <svg className="back-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Home
      </Link>
      
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="brand-title">EmailBrain</h1>
          <h2 className="auth-title">Welcome Back!</h2>
          <p className="auth-subtitle">Sign in to continue to your dashboard</p>
        </div>

        {/* Promo Code Banner */}
        {promoApplied && (
          <div style={{
            padding: '15px',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            color: 'white',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontWeight: 'bold' }}>
              🎁 Promo Code Applied!
            </p>
            <p style={{ margin: '5px 0 0', fontSize: '14px' }}>
              You'll get <strong>20% off</strong> for <strong>2 months free!</strong>
            </p>
            <p style={{ margin: '5px 0 0', fontSize: '12px', opacity: 0.9 }}>
              Code: <strong>{promoCode}</strong>
            </p>
          </div>
        )}

        {/* Invite Banner */}
        {inviteMode && (
          <div style={{
            padding: '15px',
            background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
            color: 'white',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontWeight: 'bold' }}>🎉 You've been invited to join a team!</p>
            <p style={{ margin: '5px 0 0', fontSize: '14px' }}>Sign in to accept the invitation.</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {/* Email Field */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email Address <span className="required">*</span></label>
            <input 
              id="email" 
              name="email" 
              type="email" 
              autoComplete="email" 
              required 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              className="form-input" 
              placeholder="you@company.com" 
              disabled={loading} 
            />
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">Password <span className="required">*</span></label>
            <div className="password-input-wrapper">
              <input 
                id="password" 
                name="password" 
                type={showPassword ? "text" : "password"} 
                autoComplete="current-password" 
                required 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                className="form-input password-input" 
                placeholder="Enter your password" 
                disabled={loading} 
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="password-toggle" disabled={loading}>
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {/* Forgot Password */}
          <div className="forgot-password-container">
            <Link to="/forgot-password" className="forgot-password-link">Forgot password?</Link>
          </div>

          {/* Submit Button */}
          <button type="submit" disabled={loading} className="submit-button">
            {loading ? (
              <span className="button-loading">
                <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="spinner-path" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : 'Sign In'}
          </button>
        </form>

        {/* Social Login */}
        <div className="social-login-section">
          <div className="divider">
            <div className="divider-line"></div>
            <div className="divider-text">Or continue with</div>
          </div>
          <div className="social-buttons">
            <button type="button" onClick={() => window.location.href = `${import.meta.env.VITE_API_URL}/auth/google`} disabled={loading} className="social-button">
              <svg className="social-icon" viewBox="0 0 24 24"><path fill="currentColor" d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/></svg>
              <span>Google</span>
            </button>
            <button type="button" onClick={() => window.location.href = `${import.meta.env.VITE_API_URL}/auth/github`} disabled={loading} className="social-button">
              <svg className="social-icon" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd"/></svg>
              <span>GitHub</span>
            </button>
          </div>
        </div>

        <div className="mode-toggle">
          <p className="mode-toggle-text">
            Don't have an account?{' '}
            <Link to="/signup" className="mode-toggle-link">Sign up</Link>
          </p>
        </div>

        <p className="legal-text">By continuing, you agree to our <Link to="/terms" className="legal-link">Terms of Service</Link> and <Link to="/privacy" className="legal-link">Privacy Policy</Link></p>
      </div>
    </div>
  )
}